import { Suspense, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, OrbitControls } from '@react-three/drei';
import { Veranda, Toolbar } from '../components';
import { SideDrawer } from '../components/Toolbar/SideDrawer';
import { ConfigurationForm } from './Toolbar/ConfigurationForm';
import { useFormStore } from '../state/FormStore';
import NewVerandaCalculator from '../state/NewCalculations';
import axios from 'axios';
import { SubConfigurationForm } from './Toolbar/SubConfigurationForm';
import { Vector3 } from 'three';
import { sendWebhook, getWebhookUrlFromParams } from '../utils/webhook';
import { t } from '../utils/i18n';
import VendorPrices from './VendorPrices';

const ENVIRONMENT_HDRI_URL = '/sky/venice_sunset_1k.hdr';

const desktopCameraSettings = {
    position: [-1.5, 3, 2],
    fov: 25,
    near: 0.1,
    far: 10
};

const mobileCameraSettings = {
    position: [-3.5, 3, 2],
    fov: 35,
    near: 0.1,
    far: 10
};

const lerp = (start, end, t) => {
    return start + (end - start) * t;
};

const CameraAnimation = ({ targetPosition }) => {
    const vec = new Vector3();

    useFrame((state) => {
        // Animate camera position towards the target position
        const targetX = lerp(state.camera.position.x, targetPosition[0], 0.05);
        const targetY = lerp(state.camera.position.y, targetPosition[1], 0.05);
        const targetZ = lerp(state.camera.position.z, targetPosition[2], 0.05);

        state.camera.position.set(targetX, targetY, targetZ);

        // Optionally, update camera rotation or look at a specific point
        state.camera.lookAt(vec.set(0, 0, 0));
    });

    return null;
};

const transformFormData = (originalFormData) => ({
    // Flat values used by NewVerandaCalculator for width/depth lookup
    breedte: originalFormData.maat?.breedte,
    uitval:  originalFormData.maat?.uitval,
    uitvalType: originalFormData.uitval_type?.uitval_type_option,
    kleur:   originalFormData.kleur?.kleur_option,
    glassWidth: 80, // CONST

    // Pass full nested objects — NewCalculations reads .dak_option, .goot_option etc.
    dak:       originalFormData.dak,
    gutter:    originalFormData.gutter,
    voorkant:  originalFormData.voorkant,
    linker:    originalFormData.linker,
    rechter:   originalFormData.rechter,
    zonwering: originalFormData.zonwering,
    verlichting: originalFormData.verlichting,
});

const Carousel = () => {
    const setActive = useFormStore(state => state.setActive);
    const activeTab = useFormStore(state => state.activeTab);
    const { formData } = useFormStore();

    // Check if we're in standalone doors mode
    const isStandaloneDoors = formData?.purchase_mode?.mode_option === 'Standalone Doors Only';

    const handleTabClick = (tabName) => {
        setActive(tabName);
        changeCameraPosition(tabName);
    };

    const changeCameraPosition = (tabName) => {
        // Add logic for changing camera position based on tabName
    };

    useEffect(() => {
        const rootFieldsets = document.querySelectorAll('.darzo-input fieldset[id^="root_"]');
        rootFieldsets.forEach(fieldset => {
            fieldset.style.display = 'none';
        });

        const activeFieldset = document.querySelector(`.darzo-input #root_${activeTab}`);
        if (activeFieldset) {
            activeFieldset.style.display = 'flex';
        }
    }, [activeTab]);


    useEffect(() => {
        // Sub Configurator
        /*     const rootFieldsets = document.querySelectorAll('.sub-darzo-input fieldset[id^="root_"]');
            rootFieldsets.forEach(fieldset => {
                fieldset.style.display = 'none';
            }); */

        if (activeTab === 'voorkant' && formData?.voorkant?.schuifdeur === 'Aluminium Schuifdeur') {
            const subOptionsFieldset = document.querySelector('.sub-darzo-input #root_voorkant_alum_schuifdeur_options');
            if (subOptionsFieldset) {
                if (formData.maat.breedte <= 400) {
                    subOptionsFieldset.style.display = 'block';
                }
            }
        }
        if (activeTab === 'rechter' && formData?.rechter?.rechter_wand_option === 'Aluminium Schuifdeur') {
            const subOptionsFieldset = document.querySelector('.sub-darzo-input #root_rechter_alum_schuifdeur_options');
            if (subOptionsFieldset) {
                if (formData.maat.uitval <= 400) {
                    subOptionsFieldset.style.display = 'block';
                }
            }
        }
        if (activeTab === 'linker' && formData?.linker?.linker_wand_option === 'Aluminium Schuifdeur') {
            const subOptionsFieldset = document.querySelector('.sub-darzo-input #root_linker_alum_schuifdeur_options');
            if (subOptionsFieldset) {
                if (formData.maat.uitval <= 400) {
                    subOptionsFieldset.style.display = 'block';
                }

            }
        }

    }, [activeTab, formData]);



    // All available tabs
    const allItems = [
        { id: 0, label: 'Purchase Mode', key: 'purchase_mode' },
        { id: 1, label: 'Maat', key: 'maat' },
        { id: 2, label: 'Uitval Type', key: 'uitval_type' },
        { id: 3, label: 'Kleur', key: 'kleur' },
        { id: 4, label: 'Dak', key: 'dak' },
        { id: 5, label: 'Zonwering', key: 'zonwering' },
        { id: 6, label: 'Gutter', key: 'gutter' },
        { id: 7, label: 'Voorkant', key: 'voorkant' },
        { id: 8, label: 'Linker', key: 'linker' },
        { id: 9, label: 'Rechter', key: 'rechter' },
    ];

    // Filter items based on purchase mode
    const items = isStandaloneDoors
        ? allItems.filter(item => ['purchase_mode', 'maat', 'kleur', 'voorkant', 'linker', 'rechter'].includes(item.key))
        : allItems;

    return (
        <div className="w-full overflow-x-scroll no-scrollbar mobile-toolbar">
            <div className="flex space-x-2">
                {items.map(item => (
                    <button
                        key={item.id}
                        className="flex flex-col items-center justify-center p-2 bg-gray-900 rounded-lg focus:outline-none"
                        onClick={() => handleTabClick(item.label.toLowerCase().replace(/ /g, '_'))}
                    >
                        <span className="mb-1"><img src={`icons/${item.label.toLowerCase().replace(/ /g, '_')}.png`} alt={t(item.label)} /></span>
                        <span className="text-xs text-white">{t(item.label)}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

const MobileScrollableButtons = ({ productData }) => (
    <div className="fixed bottom-0 left-0 right-0">
        <Carousel />
        <div className="overflow-x-scroll mobile-configuration-form">
            <div className="mobile-configuration-form-content text-white no-scrollbar">
                <ConfigurationForm productData={productData} />
            </div>
        </div>
        <div className="overflow-x-scroll mobile-configuration-form">
            <div className="mobile-configuration-form-content text-white no-scrollbar">
                <SubConfigurationForm />
            </div>
        </div>
    </div>
);

const DesktopContent = ({ handlePlaceOrder, totalPrice, productID, shouldAnimate, targetPosition }) => (
    <div className="content-container text-gray-700 bg-gray-200 h-full w-full">
        <div className="darzo-craft-container h-screen relative" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, position: 'relative' }}>
                <Suspense >
                    <Canvas shadows dpr={[1, 1]} style={{ background: '#606074', width: '100%', height: '100%' }} camera={desktopCameraSettings}>
                        <Suspense fallback={null}>
                            <directionalLight castShadow intensity={0.1} position={[2, 0, 0]} />
                            <Veranda />
                            <Environment files={ENVIRONMENT_HDRI_URL} blur={0} background />
                            {shouldAnimate && <CameraAnimation targetPosition={targetPosition} />}
                        </Suspense>
                        <OrbitControls enablePan enableZoom enableRotate enableDamping dampingFactor={0.04} minDistance={0.2} maxDistance={6} maxPolarAngle={Math.PI / 1.8} />
                    </Canvas>
                </Suspense>
            </div>
            <div style={{ flexShrink: 0 }}>
                <VendorPrices />
                <div className="bg-gray-100 px-4 py-2 border-t border-gray-300 flex justify-center">
                    {
                        productID ? <div className="order-info">
                            <span className="totalPrice mr-4"><span className="currency">€</span><span className="amount">{totalPrice}</span></span>
                            <button onClick={handlePlaceOrder}>{t('Place an order')}</button>
                        </div> : <div className="order-info">
                            <button onClick={handlePlaceOrder} className=''>{t('Compare prices')}</button>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
);



const MobileContent = ({ handlePlaceOrder, totalPrice, productID, shouldAnimate, targetPosition, productData }) => (
    <div className="content-container text-gray-700 bg-gray-200 h-full w-full">
        <div className="darzo-craft-container h-screen relative" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="mobile-canvas-container" style={{ flex: 1, position: 'relative' }}>
                {
                    productID ? <div className="order-info absolute top-4 right-4 z-10">
                        <span className="totalPrice"><span className="currency">€</span><span className="amount">{totalPrice}</span></span>
                        <button onClick={handlePlaceOrder}>{t('Place an order')}</button>
                    </div> : <div className="order-info absolute top-4 right-4 z-10">
                        <button onClick={handlePlaceOrder}>{t('Compare prices')}</button>
                    </div>
                }
                <Canvas shadows dpr={[1, 1]} style={{ background: '#606074', width: '100%', height: '100%' }} camera={mobileCameraSettings}>
                    <Suspense fallback={null}>
                        <directionalLight castShadow intensity={0.1} position={[2, 0, 0]} />
                        <Veranda />
                        <Environment files={ENVIRONMENT_HDRI_URL} blur={1} background />
                        {shouldAnimate && <CameraAnimation targetPosition={targetPosition} />}
                    </Suspense>
                    <OrbitControls enablePan enableZoom enableRotate enableDamping dampingFactor={0.04} minDistance={1} maxDistance={6} maxPolarAngle={Math.PI / 1.8} />
                </Canvas>
            </div>
            <div style={{ flexShrink: 0 }}>
                <VendorPrices />
                <div className="mobile-buttons-container">
                    <MobileScrollableButtons productData={productData} />
                </div>
            </div>
        </div>
    </div>
);

const Configurator = ({ product_id, cart_id, draft_id }) => {
    const { formData, setFormData } = useFormStore();
    const activeTab = useFormStore(state => state.activeTab);

    const [productData, setProductData] = useState(null);
    const [totalPrice, setTotalPrice] = useState(0);
    const [pricesFetched, setPricesFetched] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState(null);
    const [priceBreakdown, setPriceBreakdown] = useState(null);

    const [targetPosition, setTargetPosition] = useState([0, 0, 0]);
    const [shouldAnimate, setShouldAnimate] = useState(false);



    useEffect(() => {
        // Define a mapping of target positions based on layout and active tab
        const positionMapping = {
            mobile: {
                'voorkant': [-0, 5, 4],
                'linker': [-3, 1, 2],
                'rechter': [3, 1, 2],
                // Add more tabs as needed
                default: mobileCameraSettings.position
            },
            desktop: {
                'voorkant': [0, 1, 4],
                'linker': [-3, 1, 2],
                'rechter': [3, 1, 2],
                'gutter': [2, 1, 0],
                // Add more tabs as needed
                default: desktopCameraSettings.position,
            },
        };

        // Determine layout type
        const layoutType = isMobileLayout ? 'mobile' : 'desktop';

        // Get the target position based on activeTab and layout type
        const newPosition = positionMapping[layoutType][activeTab] || positionMapping[layoutType].default;

        //console.log(newPosition);
        // Trigger camera animation and update target position
        setShouldAnimate(true);
        setTargetPosition(newPosition);

        // Reset animation flag after a delay
        const timer = setTimeout(() => setShouldAnimate(false), 2000);

        return () => clearTimeout(timer);
    }, [isMobileLayout, activeTab]);

    useEffect(() => {
        // Get webhook URL from URL parameters
        const webhook = getWebhookUrlFromParams();
        if (webhook) {
            setWebhookUrl(webhook);
            console.log('Webhook URL set:', webhook);
        }
    }, []);

    const handlePlaceOrder = async () => {
        // Dynamically create tableData from formData
        const tableData = [
            { name: 'Breedte', value: formData?.maat?.breedte ?? 'N/A' },
            { name: 'Uitval', value: formData?.maat?.uitval ?? 'N/A' },
            { name: 'Doorloop Hoogte', value: formData?.maat?.doorloop_hoogte ?? 'N/A' },
            { name: 'Uitval Type', value: formData?.uitval_type?.uitval_type_option ?? 'N/A' },
            { name: 'Kleur', value: formData?.kleur?.kleur_option ?? 'N/A' },
            { name: 'Dak', value: formData?.dak?.dak_option ?? 'N/A' },
            { name: 'Zonwering', value: formData?.zonwering?.zonwering_option ?? 'N/A' },
            { name: 'Linker Wand', value: formData?.linker?.linker_wand_option ?? 'N/A' },
            { name: 'Linker Spie', value: formData?.linker?.linker_spie_option ?? 'N/A' },
            { name: 'Rechter Wand', value: formData?.rechter?.rechter_wand_option ?? 'N/A' },
            { name: 'Rechter Spie', value: formData?.rechter?.rechter_spie_option ?? 'N/A' },
            { name: 'Goot', value: formData?.gutter?.goot_option ?? 'N/A' },
            { name: 'Schuifdeur', value: formData?.voorkant?.schuifdeur ?? 'N/A' },
            { name: 'Schuifdeur Handle', value: formData?.voorkant_alum_schuifdeur_options?.voorkant_schuifdeur_handle ?? 'N/A' },
            { name: 'Total Price', value: formData?.total?.price ?? 'N/A' },
        ];

        const transformedData = {
            data: formData,
            price: totalPrice,
            priceBreakdown: priceBreakdown,
            cart_id: draft_id ? draft_id : (cart_id ? cart_id : null),
            resource_id: product_id,
            labeledData: tableData, // Pass the dynamically generated tableData
            timestamp: new Date().toISOString(),
            source: window.location.href
        };

        try {
            // Always send to the default API endpoint
            const response = await axios.post('https://api.verandadealer.nl/api/webhook/configurator/ingress', transformedData, {
                headers: { 'Content-Type': 'application/json' }
            });
            console.log('Order placed successfully:', response.data);

            // If a webhook URL is provided, also send the data there
            if (webhookUrl) {
                const webhookResponse = await sendWebhook(webhookUrl, transformedData);
                console.log('Webhook response:', webhookResponse);
            }
        } catch (error) {
            console.error('Error placing order:', error);
        }
    };

    useEffect(() => {
        // Fetch product data when product_id changes or on mount
        setPricesFetched(false);
        if (product_id) {
            const fetchProductData = async () => {
                try {
                    console.log('Fetching product data for ID:', product_id);
                    const product = await NewVerandaCalculator.fetchProductData(product_id);
                    console.log('Product data fetched successfully:', product);

                    if (!product) {
                        console.error('Product data is null or undefined');
                        alert('Error: Product not found. Please check the product_id.');
                        return;
                    }

                    if (!product.pricing_data) {
                        console.warn('Warning: Product has no pricing_data. Prices will be 0.');
                        console.warn('Product:', product);
                    }

                    setProductData(product);
                    setPricesFetched(true);
                } catch (error) {
                    console.error("Error fetching product data:", error);
                    console.error("Error details:", error.response?.data || error.message);

                    // Show user-friendly error
                    if (error.response?.status === 404) {
                        alert(`Product with ID ${product_id} not found. Please check the product_id parameter.`);
                    } else if (error.message.includes('Network Error')) {
                        alert('Cannot connect to the backend API. Please check if the backend server is running.');
                    } else {
                        alert(`Error loading product: ${error.message}`);
                    }

                    setPricesFetched(true);
                }
            };
            fetchProductData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product_id]);

    useEffect(() => {
        // Recalculate prices when formData or productData changes
        if (pricesFetched && productData) {
            try {
                const transformedFormData = transformFormData(formData);
                const calculator = new NewVerandaCalculator(transformedFormData, productData);
                const result = calculator.calculateTotalPrice();

                console.log('Price calculation result:', result);
                setTotalPrice(result.total);
                setPriceBreakdown(result.breakdown);
            } catch (error) {
                console.error("Error calculating prices:", error);
                setTotalPrice(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, productData, pricesFetched]);

    const fetchDraftState = async () => {

        try {
            const response = await fetch(`https://api.verandadealer.nl/api/configurator/state/${draft_id}`);
            const data = await response.json();

            // Add checks to ensure data.data and data.data.data are not null or undefined
            if (data.success) {

                if (data.data && data.data.data) {
                    const updatedFormData = {
                        ...formData,
                        ...data.data.data
                    };
                    setFormData(updatedFormData);
                }

                //setPricesFetched(true);
                handlePlaceOrder(); // Trigger handlePlaceOrder after fetching draft state
            } else {
                console.error('Failed to fetch configurator state:', data.message);
            }
        } catch (error) {
            console.error('Error fetching configurator state:', error);
        }
    };


    useEffect(() => {
        const handleResize = () => setIsMobileLayout(window.innerWidth < 1000);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (draft_id) fetchDraftState();
    }, [draft_id]);


    return (
        <div className={`darzo-craft-body flex ${isMobileLayout ? 'flex-col-reverse' : 'h-screen'}`}>
            {!isMobileLayout && (
                <div className="sidebar w-5/12">
                    <div className="flex">
                        <Toolbar />
                        <SideDrawer productData={productData} />
                    </div>
                </div>
            )}
            {isMobileLayout ?
                <MobileContent handlePlaceOrder={handlePlaceOrder} totalPrice={totalPrice} productID={product_id} shouldAnimate={shouldAnimate} targetPosition={targetPosition} productData={productData} /> :
                <DesktopContent handlePlaceOrder={handlePlaceOrder} totalPrice={totalPrice} productID={product_id} shouldAnimate={shouldAnimate} targetPosition={targetPosition} />}
        </div>
    );
};

export default Configurator;
